/**
 * Interconnection — per-type preferences sub-form (Workflow Prompt 2).
 * Required: bandwidthGbps (sets the analysis size).
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import type { InterconnectionPreferences } from '../../../types/siteRag';
import { FieldRow } from './shared';
import { validateInterconnectionPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateInterconnectionPreferences };

interface Props {
  value: InterconnectionPreferences | undefined;
  onChange: (next: InterconnectionPreferences) => void;
}

export const InterconnectionPreferencesForm: React.FC<Props> = ({ value: rawValue, onChange }) => {
  // PB2: belt-and-braces.
  const value: InterconnectionPreferences = (rawValue ?? {}) as InterconnectionPreferences;
  const set = <K extends keyof InterconnectionPreferences>(key: K, v: InterconnectionPreferences[K]) => {
    onChange({ ...value, [key]: v });
  };
  const setLatency = (cloud: 'aws' | 'azure' | 'gcp', n: number | undefined) => {
    const next = { ...(value.latencyTargetsMs ?? {}) };
    if (n === undefined) delete next[cloud];
    else next[cloud] = n;
    onChange({ ...value, latencyTargetsMs: Object.keys(next).length > 0 ? next : undefined });
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FieldRow label="Bandwidth (Gbps)" fieldKey="bandwidthGbps" required help="Aggregate bandwidth target in Gbps. Hyperscale = 100–1000+.">
          <Input
            type="number" min={1} step={1} placeholder="e.g. 100"
            value={value.bandwidthGbps ?? ''}
            onChange={(e) => set('bandwidthGbps', e.target.value === '' ? undefined : Number(e.target.value))}
            className="h-9 text-sm"
          />
        </FieldRow>

        <FieldRow label="Carrier diversity (min)" fieldKey="carrierDiversityMin" help="Minimum unique carriers required. 2 is the Tier III minimum; 3 is hyperscale-grade.">
          <Select
            value={value.carrierDiversityMin?.toString() ?? ''}
            onValueChange={(v) => set('carrierDiversityMin', v === '' ? undefined : (Number(v) as 1 | 2 | 3))}
          >
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 — single carrier</SelectItem>
              <SelectItem value="2">2 — Tier III minimum</SelectItem>
              <SelectItem value="3">3 — hyperscale-grade</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
      </div>

      <div className="border-t border-border pt-4 space-y-3">
        <div data-testid="pref-field-latencyTargetsMs">
          <Label className="type-section text-text-secondary mb-2 block">
            Latency targets (ms RTT)
          </Label>
          <div className="text-[10px] text-text-muted mb-2">
            Round-trip latency targets per cloud region. Leave blank to skip the per-cloud check.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(['aws', 'azure', 'gcp'] as const).map((cloud) => (
              <FieldRow key={cloud} label={cloud.toUpperCase()}>
                <Input
                  type="number" min={1} step={1} placeholder="e.g. 10"
                  value={value.latencyTargetsMs?.[cloud] ?? ''}
                  onChange={(e) => setLatency(cloud, e.target.value === '' ? undefined : Number(e.target.value))}
                  className="h-9 text-sm"
                />
              </FieldRow>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between" data-testid="pref-field-ixPeeringRequired">
          <div>
            <Label className="type-section text-text-secondary">IX peering required</Label>
            <div className="text-[10px] text-text-muted">
              Direct peering at a public IX must be feasible. Drives the IX-proximity verdict.
            </div>
          </div>
          <Switch
            checked={!!value.ixPeeringRequired}
            onCheckedChange={(b) => set('ixPeeringRequired', b)}
          />
        </div>

        <div className="flex items-center justify-between" data-testid="pref-field-submarineCableProximityImportant">
          <div>
            <Label className="type-section text-text-secondary">Submarine cable proximity important</Label>
            <div className="text-[10px] text-text-muted">
              Coastal site, international traffic. Triggers the submarine cable section.
            </div>
          </div>
          <Switch
            checked={!!value.submarineCableProximityImportant}
            onCheckedChange={(b) => set('submarineCableProximityImportant', b)}
          />
        </div>
      </div>
    </div>
  );
};
